import type { JSX, QRL } from '@builder.io/qwik';
import { Slot, useContext } from '@builder.io/qwik';
import { component$ } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import {
  getCSSBorderRadius,
  getCSSButtonBorderWidth,
  getCSSColorBg,
  getCSSColorBorder,
  getCSSColorHoverBg,
  getCSSColorHoverBorder,
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import type {
  BORDER_RADIUS,
  BORDER_WIDTH,
} from '~/types/AppData/Theme/BorderInterface';

const buttonClasses = cva(
  'min-w-[120px] py-2 text-center inline-flex justify-center',
  {
    variants: {
      background: getCSSColorBg(),
      backgroundHover: getCSSColorHoverBg(),
      text: getCSSColorText(),
      textHover: getCSSColorText(),
      border: getCSSColorBorder(),
      borderHover: getCSSColorHoverBorder(),
      borderWidth: getCSSButtonBorderWidth(),
      borderRadius: getCSSBorderRadius(),
      sizeSm: getCSSSizeSmText(),
      sizeMd: getCSSSizeMdText(),
      sizeLg: getCSSSizeLgText(),
      size: {
        default: 'px-5',
        lg: 'px-20',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      customRadius: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        '3xl': 'rounded-3xl',
        full: 'rounded-full',
        pill: 'rounded-[40px]',
      },
      isDisabled: {
        true: 'cursor-default',
        false: 'cursor-pointer',
      },
    },
  }
);

type AccessibilityButtonType = {
  variant?: string;
  size?: 'default' | 'lg';
  target?: '_blank' | '_self';
  url?: string;
  ariaLabel?: string;
  testId?: string;
  id?: string;
  disabled?: boolean;
  onClick$?: QRL<() => void>;
  fullWidth?: boolean;
  borderRadius?:
    | 'none'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | 'full'
    | 'pill';
  class?: string;
};

export default component$<AccessibilityButtonType>(
  ({
    variant = 'primary',
    size = 'default',
    ariaLabel,
    testId,
    id,
    disabled,
    onClick$,
    target = '_self',
    url = '',
    fullWidth = false,
    borderRadius,
    class: additionalClasses = '',
  }): JSX.Element => {
    const {
      themeComponents: { button },
    } = useContext(AppDataContext);

    const theClasses = buttonClasses({
      background:
        variant === 'disabled'
          ? 'grey'
          : variant === 'primary'
            ? button.backgroundColorNormal.value
            : button.backgroundColorNormalSecondary.value,
      backgroundHover:
        variant === 'disabled'
          ? 'grey'
          : variant === 'primary'
            ? button.backgroundColorHover.value
            : button.backgroundColorHoverSecondary.value,
      text:
        variant === 'disabled'
          ? 'white'
          : variant === 'primary'
            ? button.textColorNormal.value
            : button.textColorNormalSecondary.value,
      textHover:
        variant === 'disabled'
          ? 'white'
          : variant === 'primary'
            ? button.textColorHover.value
            : button.textColorHoverSecondary.value,
      border:
        variant === 'primary'
          ? button.borderColorNormal.value
          : button.borderColorNormalSecondary.value,
      borderHover:
        variant === 'primary'
          ? button.borderColorHover.value
          : button.borderColorHoverSecondary.value,
      borderWidth: button.borderWidthNormal.value as unknown as BORDER_WIDTH,
      borderRadius: button.borderRadius.value as unknown as BORDER_RADIUS,
      isDisabled: variant === 'disabled',
      size,
      fullWidth,
      customRadius: borderRadius,
      ...getFontSizeObject(button, 'fontSize'),
    });

    const finalClasses = `${theClasses} ${additionalClasses}`.trim();

    if (onClick$ !== undefined) {
      return (
        <button
          disabled={disabled}
          class={finalClasses}
          type='button'
          id={id}
          aria-label={ariaLabel}
          data-testid={testId}
          onClick$={onClick$}
        >
          <Slot />
        </button>
      );
    }

    return (
      <a
        id={id}
        href={url}
        target={target}
        class={finalClasses}
        data-testid={testId}
      >
        <Slot />
      </a>
    );
  }
);
